import { Injectable } from '@angular/core';
import { MatSnackBar, MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';
import { NotificationSnackComponent, NotificationData, NotificationType } from '../../components/notification-snack/notification-snack.component';

const DEFAULT_DURATIONS: Record<NotificationType, number> = {
  success: 3500,
  info:    5000,
  warning: 6000,
  error:   8000,
};

const TITLES: Record<NotificationType, string> = {
  success: 'Éxito',
  info:    'Información',
  warning: 'Advertencia',
  error:   'Error',
};

/** Tiempo mínimo (ms) que debe pasar antes de volver a mostrar la misma clave */
const COOLDOWN_MS = 8000;

@Injectable({
  providedIn: 'root'
})
export class SnackBarService {

  /** Claves de notificaciones actualmente visibles en pantalla */
  private _active = new Set<string>();

  /** Timestamp de la última vez que se mostró cada clave */
  private _lastShown = new Map<string, number>();

  constructor(private _snackBar: MatSnackBar) {}

  /**
   * Muestra una notificación tipada con icono, título y barra de progreso.
   * Si ya hay una activa con la misma clave (tipo + primeras palabras del mensaje),
   * la descarta silenciosamente. También respeta un cooldown entre apariciones.
   */
  show(type: NotificationType, message: string, title?: string, duration?: number): MatSnackBarRef<NotificationSnackComponent> | null {
    const key = `${type}::${message.slice(0, 40)}`;
    const now = Date.now();

    // Deduplicación: si ya está visible o en cooldown, no mostrar
    if (this._active.has(key)) { return null; }
    const last = this._lastShown.get(key);
    if (last && now - last < COOLDOWN_MS) { return null; }

    const dur = duration ?? DEFAULT_DURATIONS[type];
    const data: NotificationData = {
      type,
      title: title ?? TITLES[type],
      message,
      duration: dur,
    };

    this._active.add(key);
    this._lastShown.set(key, now);

    const ref = this._snackBar.openFromComponent(NotificationSnackComponent, {
      data,
      duration: dur,
      verticalPosition: 'top',
      horizontalPosition: 'right',
      panelClass: ['toast-panel', `notif-${type}`],
    });

    ref.afterDismissed().subscribe(() => this._active.delete(key));
    return ref;
  }

  close(ref: MatSnackBarRef<NotificationSnackComponent>): void {
    if (ref) { ref.dismiss(); }
  }

  openSuccess(message: string, title?: string, duration?: number): void {
    this.show('success', message, title, duration);
  }

  openWarning(message: string, title?: string, duration?: number): void {
    this.show('warning', message, title, duration);
  }

  openError(message: string, title?: string, duration?: number): void {
    this.show('error', message, title, duration);
  }

  /**
   * @deprecated Usar openSuccess / openWarning / openError / openInfo.
   * Mantenido por compatibilidad con el código existente.
   */
  openSnackBar(message: string, action: string, time: number): void {
    this._snackBar.open(message, action || 'Ok', {
      duration: time,
      verticalPosition: 'top',
      panelClass: ['success-snack-bar'],
    });
  }

  /** @deprecated Usar openSuccess() */
  showSuccess(message: string): void {
    this.openSuccess(message);
  }
}
